/**
 * Project Completion Check Workflow
 *
 * This workflow handles automatic project completion detection:
 * 1. Monitors task completion events
 * 2. Analyzes if all project tasks are completed
 * 3. Updates project status to complete
 * 4. Notifies user of project completion
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const projectCompletionCheckWorkflow: Workflow = {
  id: 'project-completion-check',
  name: 'Controllo Completamento Progetto',
  description: 'Monitora il completamento dei task e aggiorna automaticamente lo stato del progetto quando tutti i task sono completati',
  trigger: {
    type: 'event',
    config: {
      // Trigger when a task is updated (potentially completed)
      eventType: 'entity.updated',
      entityType: 'task',
      // Only trigger if status changed to completed
      conditions: {
        field: 'status',
        value: 'completed',
      },
    },
  },
  steps: [
    {
      id: 'check_completion',
      name: 'Verifica Completamento',
      type: 'ai_analyze',
      config: {
        prompt: `Analizza lo stato dei task di questo progetto.

Verifica:
- Numero totale task
- Numero task completati
- Percentuale completamento
- Se ci sono task bloccanti ancora aperti

Rispondi in formato JSON:
{
  "totalTasks": number,
  "completedTasks": number,
  "completionPercentage": number,
  "allTasksCompleted": boolean,
  "blockingTasksOpen": boolean
}`,
        includeProjectContext: true,
      },
      next: ['update_project'],
      condition: {
        field: 'allTasksCompleted',
        operator: 'equals',
        value: true,
      },
    },
    {
      id: 'update_project',
      name: 'Aggiorna Progetto',
      type: 'update_project',
      config: {
        // Mark project as completed
        status: 'completed',
        completedAt: 'now',
        updatePercentage: true,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Notifica Completamento',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: 'Congratulazioni! Il progetto "{projectName}" è stato completato. Tutti i {totalTasks} task sono stati chiusi.',
        includeProjectLink: true,
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate project completion percentage
 */
export function calculateCompletionPercentage(
  completedTasks: number,
  totalTasks: number
): number {
  if (totalTasks === 0) return 0;
  return Math.round((completedTasks / totalTasks) * 100);
}

/**
 * Check if project should be marked as complete
 */
export function shouldMarkAsComplete(
  completedTasks: number,
  totalTasks: number,
  blockingTasksOpen: boolean
): boolean {
  return completedTasks === totalTasks && totalTasks > 0 && !blockingTasksOpen;
}
